import type { FilterGroup, FilterSelection } from "@carbon6/c6-frontend-design";
import {
  CURATED_FACETS,
  DATASET_FILTER_PRESETS,
  isPresetActive,
  type DatasetFilterPreset,
} from "./datasetFilters";

// 필터 레일에 항상 표시할 컨트롤 참조다. 값(선택 상태)은 저장하지 않고 노출 목록만 저장한다.
//   "preset:<id>"  : use-case 프리셋 토글(DATASET_FILTER_PRESETS 의 id)
//   "facet:<key>"  : facet 컨트롤(datasetFilters.ts curated key)
export type FilterBookmark = `preset:${string}` | `facet:${string}`;

// 화면마다 따로 저장한다. Library/Archived 는 같은 화면(DatasetPage)이라 키를 함께 쓴다.
export type DatasetFilterSurface = "datasetLibrary" | "review";

const PRESET_IDS: ReadonlySet<string> = new Set(DATASET_FILTER_PRESETS.map((p) => p.id));
// 프리셋 전용 facet(environment·arms)은 컨트롤이 없어 북마크 대상이 아니다. 이전에 저장한 값에 있어도 버린다.
const FACET_KEYS: ReadonlySet<string> = new Set(
  CURATED_FACETS.filter((f) => f.section !== "preset").map((f) => f.key),
);

// 기본값은 프리셋 5종과 작업·하드웨어·수집·품질의 자주 쓰는 축이다. 전체 프리셋에서 만들지 않고 명시 목록으로 둔다.
// 그래서 새로 추가하는 프리셋은 기본 레일에 자동으로 들어가지 않는다. 사용자가 시트에서 ★ 로 추가한다.
export function defaultFilterBookmarks(): readonly FilterBookmark[] {
  return [
    "preset:bimanual",
    "preset:single",
    "preset:real",
    "preset:sim",
    "preset:open_source",
    "facet:task",
    "facet:embodiment",
    "facet:recording_mode",
    "facet:source",
    "facet:episode_quality_candidate",
    "facet:episode_quality",
  ];
}

export function isPresetBookmark(bookmark: FilterBookmark): boolean {
  return bookmark.startsWith("preset:");
}

export function bookmarkTargetId(bookmark: FilterBookmark): string {
  return bookmark.slice(bookmark.indexOf(":") + 1);
}

// 알 수 없는 참조(프리셋 id·facet key 변경 등)는 경고 없이 버리고, 남은 항목을 쓴다.
function isKnownBookmark(entry: unknown): entry is FilterBookmark {
  if (typeof entry !== "string") {
    return false;
  }
  if (entry.startsWith("preset:")) {
    return PRESET_IDS.has(entry.slice("preset:".length));
  }
  if (entry.startsWith("facet:")) {
    return FACET_KEYS.has(entry.slice("facet:".length));
  }
  return false;
}

export type AddBookmarkResult =
  | { ok: true; bookmarks: readonly FilterBookmark[] }
  | { ok: false; reason: "duplicate" };

// 순수 헬퍼다. 저장은 호출하는 쪽(saveFilterBookmarks)이 한다.
export function addFilterBookmark(
  bookmarks: readonly FilterBookmark[],
  entry: FilterBookmark,
): AddBookmarkResult {
  if (bookmarks.includes(entry)) {
    return { ok: false, reason: "duplicate" };
  }
  return { ok: true, bookmarks: [...bookmarks, entry] };
}

export function removeFilterBookmark(
  bookmarks: readonly FilterBookmark[],
  entry: FilterBookmark,
): readonly FilterBookmark[] {
  return bookmarks.filter((bookmark) => bookmark !== entry);
}

// 레일 표시 모델이다. 북마크와 현재 필터 스냅샷에서 다음을 계산한다:
// - 레일 컨트롤(프리셋·facet)
// - "레일 밖 조건": 선택이 있지만 레일의 어떤 컨트롤도 그 상태를 보여주지 못하는 그룹
// 레일(DatasetFilter)과 고급 필터 진입 버튼(배지)이 같은 계산을 함께 쓴다.
export type FilterRailModel = {
  presets: readonly DatasetFilterPreset[];
  facetGroups: readonly FilterGroup[];
  appliedOutside: readonly FilterGroup[];
  outsideCount: number;
};

export function resolveFilterRail(
  bookmarks: readonly FilterBookmark[],
  groups: readonly FilterGroup[],
  selection: FilterSelection,
  labelTokenCount: number,
): FilterRailModel {
  const presets = bookmarks
    .filter(isPresetBookmark)
    .map((entry) => DATASET_FILTER_PRESETS.find((preset) => preset.id === bookmarkTargetId(entry)))
    .filter((preset): preset is DatasetFilterPreset => preset !== undefined);
  // 카탈로그에 없는 facet(옵션 0개)은 현재 목록에서 건너뛴다.
  const facetGroups = bookmarks
    .filter((entry) => !isPresetBookmark(entry))
    .map((entry) => groups.find((group) => group.value === bookmarkTargetId(entry)))
    .filter((group): group is FilterGroup => group !== undefined);

  const railFacetKeys = new Set(facetGroups.map((group) => group.value));
  const presetCoveredKeys = new Set(
    presets
      .filter((preset) => isPresetActive(preset, selection))
      .flatMap((preset) => Object.keys(preset.selection)),
  );
  const appliedOutside = groups.filter(
    (group) =>
      (selection[group.value] ?? []).length > 0 &&
      !railFacetKeys.has(group.value) &&
      !presetCoveredKeys.has(group.value),
  );
  return {
    presets,
    facetGroups,
    appliedOutside,
    outsideCount: appliedOutside.length + (labelTokenCount > 0 ? 1 : 0),
  };
}

// uid 스코프 저장 키다. 로그아웃하거나 사용자를 바꿔도 북마크가 섞이지 않는다(teamGroupSelection 패턴).
function storageKey(uid: string | null): string {
  return `hatchery-registry:dataset-filter-bookmarks:${uid ?? "anonymous"}`;
}

type StoredBookmarks = Partial<Record<DatasetFilterSurface, readonly unknown[]>>;

export function loadFilterBookmarks(
  uid: string | null,
  surface: DatasetFilterSurface,
): readonly FilterBookmark[] {
  if (typeof window === "undefined") {
    return defaultFilterBookmarks();
  }
  try {
    const raw = window.localStorage.getItem(storageKey(uid));
    if (raw === null) {
      return defaultFilterBookmarks();
    }
    const parsed: unknown = JSON.parse(raw);
    const entries = (parsed as StoredBookmarks)[surface];
    if (!Array.isArray(entries)) {
      return defaultFilterBookmarks();
    }
    // 알 수 없는 참조(프리셋 id·facet key 변경 등)만 버린다(순서 = 레일 표시 순서).
    return entries.filter(isKnownBookmark);
  } catch {
    return defaultFilterBookmarks();
  }
}

export function saveFilterBookmarks(
  uid: string | null,
  surface: DatasetFilterSurface,
  bookmarks: readonly FilterBookmark[],
): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const raw = window.localStorage.getItem(storageKey(uid));
    const parsed: StoredBookmarks = raw !== null ? (JSON.parse(raw) as StoredBookmarks) : {};
    const next: StoredBookmarks = {
      ...parsed,
      [surface]: bookmarks,
    };
    window.localStorage.setItem(storageKey(uid), JSON.stringify(next));
  } catch {
    // 비공개 또는 제한된 브라우저 환경에서는 Storage 를 쓸 수 없을 수 있다.
  }
}
